import type { DatabaseFilterClause } from './query-types.js';
import { isPropertyValuePayload, propertyScalar, type PropertyValuePayload } from './property-value.js';

export type RowFilterContext = {
  title: string | null;
  properties: Map<string, PropertyValuePayload>;
};

function isEmptyScalar(scalar: string | number | boolean | null): boolean {
  if (scalar === null) {
    return true;
  }
  if (typeof scalar === 'string') {
    return scalar.length === 0;
  }
  if (typeof scalar === 'number') {
    return Number.isNaN(scalar);
  }
  return false;
}

function scalarEquals(
  a: string | number | boolean | null,
  b: unknown,
): boolean {
  if (a === null && (b === null || b === undefined)) {
    return true;
  }
  if (typeof a === 'boolean') {
    return a === b;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a === b;
  }
  return String(a) === String(b ?? '');
}

function optionIdsFromPayload(
  payload: PropertyValuePayload | undefined,
): string[] {
  if (!payload) {
    return [];
  }
  if (payload.t === 'select') {
    const id = payload.v == null ? '' : String(payload.v).trim();
    return id ? [id] : [];
  }
  if (payload.t === 'multi_select') {
    return Array.isArray(payload.v) ? payload.v.map(String).filter(Boolean) : [];
  }
  return [];
}

function normalizeFilterOptionIds(expected: unknown): string[] {
  if (Array.isArray(expected)) {
    return expected.map(String).filter(Boolean);
  }
  if (expected === undefined || expected === null || expected === '') {
    return [];
  }
  return [String(expected)];
}

function matchesSelectLikeClause(
  payload: PropertyValuePayload | undefined,
  op: DatabaseFilterClause['operator'],
  expected: unknown,
): boolean {
  const cellIds = optionIdsFromPayload(payload);
  const filterIds = normalizeFilterOptionIds(expected);

  switch (op) {
    case 'is_empty':
      return cellIds.length === 0;
    case 'is_not_empty':
      return cellIds.length > 0;
    case 'eq':
    case 'in':
      if (filterIds.length === 0) {
        return true;
      }
      return filterIds.some((id) => cellIds.includes(id));
    case 'neq':
      if (filterIds.length === 0) {
        return true;
      }
      return !filterIds.some((id) => cellIds.includes(id));
    default:
      return true;
  }
}

function matchesClause(ctx: RowFilterContext, clause: DatabaseFilterClause): boolean {
  const field = clause.propertyId ?? 'title';
  const payload = field === 'title' ? undefined : ctx.properties.get(field);

  if (payload && (payload.t === 'select' || payload.t === 'multi_select')) {
    return matchesSelectLikeClause(payload, clause.operator, clause.value);
  }

  const scalar =
    field === 'title'
      ? ctx.title == null || ctx.title === ''
        ? null
        : ctx.title
      : propertyScalar(payload);

  const op = clause.operator;
  const expected = clause.value;

  switch (op) {
    case 'is_empty':
      return isEmptyScalar(scalar);
    case 'is_not_empty':
      return !isEmptyScalar(scalar);
    case 'eq':
      return scalarEquals(scalar, expected);
    case 'neq':
      return !scalarEquals(scalar, expected);
    case 'contains': {
      if (scalar === null) {
        return false;
      }
      const hay = String(scalar).toLowerCase();
      const needle = String(expected ?? '').toLowerCase();
      return hay.includes(needle);
    }
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const left = typeof scalar === 'number' ? scalar : scalar == null ? null : String(scalar);
      const right =
        typeof expected === 'number' ? expected : expected == null ? null : String(expected);
      if (left === null || right === null) {
        return false;
      }
      const cmp =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right));
      if (op === 'gt') {
        return cmp > 0;
      }
      if (op === 'gte') {
        return cmp >= 0;
      }
      if (op === 'lt') {
        return cmp < 0;
      }
      return cmp <= 0;
    }
    case 'in': {
      if (!Array.isArray(expected)) {
        return false;
      }
      const s = scalar == null ? null : String(scalar);
      return expected.some((item) => String(item) === s);
    }
    default:
      return true;
  }
}

export function rowMatchesFilters(
  ctx: RowFilterContext,
  filters: DatabaseFilterClause[] | undefined,
): boolean {
  if (!filters?.length) {
    return true;
  }
  return filters.every((clause) => matchesClause(ctx, clause));
}

/** Toolbar search: matches if needle appears in title or any property (OR). */
export function rowMatchesSearch(
  ctx: RowFilterContext,
  search: string | undefined,
): boolean {
  const trimmed = search?.trim();
  if (!trimmed) {
    return true;
  }
  const needle = trimmed.toLowerCase();

  const title = ctx.title;
  if (title && title.toLowerCase().includes(needle)) {
    return true;
  }

  for (const payload of ctx.properties.values()) {
    const scalar = propertyScalar(payload);
    if (scalar === null || (typeof scalar === 'number' && Number.isNaN(scalar))) {
      continue;
    }
    if (String(scalar).toLowerCase().includes(needle)) {
      return true;
    }
  }
  return false;
}

export function propertyMapFromList(
  entries: { propertyId: string; value: unknown }[],
): Map<string, PropertyValuePayload> {
  const map = new Map<string, PropertyValuePayload>();
  for (const entry of entries) {
    if (isPropertyValuePayload(entry.value)) {
      map.set(entry.propertyId, entry.value);
    }
  }
  return map;
}
